import AppIconButton from "../../../components/form/AppIconButton";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AdaptorTypeIcon from "../../../components/AdaptorTypeIcon";
import { AppAutocomplete, AppButton, AppTextField, AppToggleField } from "../../../components/form";
import {
  AppFormField,
  AppFormGrid,
  AppSlideOver,
  PageSectionCard,
  ResponsiveActions,
} from "../../../components/layout";
import { PageEmptyState, PageHeader, PageListContainer } from "../../../components/Page";
import PackageLimitHint from "../../../components/PackageLimitHint";
import VpnProfileSelect from "../../../components/VpnProfileSelect";
import WarehouseExplainPanel from "./WarehouseExplainPanel";
import ResourceQueryStatsPanel, {
  type ResourceQueryCallRow,
} from "../../../components/ResourceQueryStatsPanel";
import {
  GRID_FULL,
  GRID_HALF,
  formRowButtonSx,
} from "../../../lib/ui/formLayout";
import {
  adminDataAdaptorService,
  flattenAdaptorLeafObjects,
  type AdaptorObjectDescribe,
  type AdaptorObjectNode,
  type DataAdaptor,
  type DataAdaptorSaveRequest,
  type DataAdaptorTestResult,
  type DataAdaptorType,
  type AdaptorAuthMode,
  isWarehouseAdaptorType,
} from "../../../services/adminDataAdaptorService";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { formatLimitValue, usePackageLimits } from "../../../hooks/usePackageLimits";

type TypeOption = { value: DataAdaptorType; label: string };
type SslOption = { value: string; label: string };

const TYPE_OPTIONS: TypeOption[] = [
  { value: "POSTGRESQL", label: "PostgreSQL" },
  { value: "MYSQL", label: "MySQL" },
  { value: "MONGODB", label: "MongoDB" },
  { value: "TRINO", label: "Trino / Presto" },
  { value: "CLICKHOUSE", label: "ClickHouse" },
  { value: "HIVE", label: "Apache Hive" },
  { value: "SPARK_SQL", label: "Spark SQL (Thrift)" },
  { value: "DATABRICKS", label: "Databricks SQL" },
  { value: "SNOWFLAKE", label: "Snowflake" },
  { value: "BIGQUERY", label: "Google BigQuery" },
  { value: "REDSHIFT", label: "Amazon Redshift" },
];

/** Auth modes per warehouse engine; first entry is the default. Engines not listed use username + password. */
const AUTH_MODES: Partial<Record<DataAdaptorType, AdaptorAuthMode[]>> = {
  TRINO: ["PASSWORD", "KERBEROS"],
  HIVE: ["PASSWORD", "KERBEROS"],
  SPARK_SQL: ["PASSWORD", "KERBEROS"],
  DATABRICKS: ["TOKEN", "OAUTH_M2M"],
  SNOWFLAKE: ["PASSWORD", "KEY_PAIR", "TOKEN"],
  BIGQUERY: ["SERVICE_ACCOUNT_JSON", "TOKEN"],
};

/** Engines whose top-level namespace the adaptor pins (Trino catalog, Databricks catalog, Snowflake database, BigQuery project). */
const CATALOG_TYPES: DataAdaptorType[] = ["TRINO", "DATABRICKS", "SNOWFLAKE", "BIGQUERY"];

/** Engines that report scanned bytes before running (EXPLAIN / dry run). ClickHouse only estimates rows. */
const SCAN_BYTES_TYPES: DataAdaptorType[] = ["TRINO", "HIVE", "SPARK_SQL", "DATABRICKS", "SNOWFLAKE", "BIGQUERY", "REDSHIFT"];

/** Warehouse drivers that cannot tunnel through a VPN profile (cloud services use private endpoints). */
const NO_VPN_TYPES: DataAdaptorType[] = ["HIVE", "SPARK_SQL", "DATABRICKS", "SNOWFLAKE", "BIGQUERY"];

const defaultAuthMode = (type: DataAdaptorType | undefined): AdaptorAuthMode | undefined =>
  type ? AUTH_MODES[type]?.[0] : undefined;

const usesKeyJson = (type: DataAdaptorType, mode: AdaptorAuthMode | undefined): boolean =>
  (type === "BIGQUERY" && mode === "SERVICE_ACCOUNT_JSON") || (type === "SNOWFLAKE" && mode === "KEY_PAIR") ||
  mode === "KERBEROS";

/** Engines with engine-side ML table functions usable for forecast push-down. */
const ENGINE_ML_TYPES: DataAdaptorType[] = ["BIGQUERY", "DATABRICKS"];

const numberOrClear = (text: string | undefined): number => {
  const n = Number(text);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};

/** "key=value" lines ↔ map. */
const parseProps = (text: string | undefined): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const line of (text ?? "").split(/\r?\n/)) {
    const idx = line.indexOf("=");
    if (idx <= 0) continue;
    const key = line.slice(0, idx).trim();
    if (key) out[key] = line.slice(idx + 1).trim();
  }
  return out;
};

const formatProps = (props: Record<string, string> | null | undefined): string =>
  Object.entries(props ?? {})
    .map(([k, v]) => `${k}=${v}`)
    .join("\n");

const BYTES_PER_GB = 1024 * 1024 * 1024;

type WarehouseFormFields = {
  catalog?: string;
  authMode?: AdaptorAuthMode;
  keyJson?: string;
  keyJsonSet?: boolean;
  httpPath?: string;
  computeWarehouse?: string;
  role?: string;
  /** "key=value" per line. */
  extraPropsText?: string;
  cacheTtlText?: string;
  timeoutText?: string;
  dailyScanGbText?: string;
  dailyQueriesText?: string;
  maxAiText?: string;
  resourceGroup?: string;
  requirePartitionFilter?: boolean;
  ragSampleValues?: boolean;
  allowEngineMl?: boolean;
  kerberosServicePrincipal?: string;
  /** Comma-separated schema names. */
  allowedSchemasText?: string;
  maxScanGb?: string;
  maxScanRowsText?: string;
  maxResultRowsText?: string;
  autoDisableEnabled?: boolean;
  minSuccessPercentText?: string;
  minCallsText?: string;
  queryCallCount?: number;
  querySuccessCount?: number;
  queryFailureCount?: number;
  autoDisabledAt?: string | null;
};

function AdaptorStatusChip({
  enabled,
  autoDisabledAt,
}: {
  enabled: boolean;
  autoDisabledAt?: string | null;
}) {
  const { t } = useTranslation();
  if (autoDisabledAt) {
    return <Chip size="small" color="warning" label={t("resourceQueryStats.autoDisabledShort")} />;
  }
  return (
    <Chip
      size="small"
      color={enabled ? "success" : "default"}
      variant={enabled ? "filled" : "outlined"}
      label={enabled ? t("adminDataAdaptors.active") : t("adminDataAdaptors.passive")}
    />
  );
}

function AdaptorStatsSummary({
  total = 0,
  success = 0,
  failure = 0,
}: {
  total?: number;
  success?: number;
  failure?: number;
}) {
  const { t } = useTranslation();
  const rate = total > 0 ? Math.round((success * 100) / total) : null;
  const columns: { label: string; value: string | number; color?: string }[] = [
    { label: t("resourceQueryStats.total"), value: total },
    { label: t("resourceQueryStats.success"), value: success, color: "success.main" },
    { label: t("resourceQueryStats.failure"), value: failure, color: "error.main" },
    { label: t("resourceQueryStats.successRate"), value: rate == null ? "—" : `${rate}%` },
  ];
  return (
    <Box
      component="table"
      sx={{
        borderCollapse: "collapse",
        width: "max-content",
        maxWidth: "100%",
        "& th, & td": {
          border: 1,
          borderColor: "divider",
          px: 0.75,
          py: 0.15,
          lineHeight: 1.25,
          fontSize: "0.7rem",
          whiteSpace: "nowrap",
          textAlign: "center",
        },
        "& th": {
          color: "text.secondary",
          fontWeight: 600,
        },
      }}
    >
      <Box component="thead">
        <Box component="tr">
          {columns.map((column) => (
            <Box component="th" key={column.label}>
              {column.label}
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        <Box component="tr">
          {columns.map((column) => (
            <Box component="td" key={column.label} sx={{ fontWeight: 700, color: column.color }}>
              {column.value}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

const queryHealthRequestFields = (form: WarehouseFormFields): Partial<DataAdaptorSaveRequest> => {
  const percent = Number(form.minSuccessPercentText);
  const calls = Number(form.minCallsText);
  return {
    autoDisableEnabled: !!form.autoDisableEnabled,
    autoDisableMinSuccessPercent:
      Number.isFinite(percent) && (form.minSuccessPercentText ?? "").trim()
        ? Math.min(100, Math.max(0, Math.round(percent)))
        : 80,
    autoDisableMinCalls: Number.isFinite(calls) && calls > 0 ? Math.round(calls) : 10,
  };
};

/** Query wait / result / scan caps — all adaptor types. Empty → 0 so the backend clears. */
const queryLimitRequestFields = (form: WarehouseFormFields): Partial<DataAdaptorSaveRequest> => {
  const rows = Number(form.maxScanRowsText);
  return {
    queryTimeoutSeconds: numberOrClear(form.timeoutText),
    maxResultRows: numberOrClear(form.maxResultRowsText),
    maxScanRows: Number.isFinite(rows) && rows > 0 ? Math.round(rows) : 0,
  };
};

/** Warehouse-only request fields; empty limits are sent as 0 so the backend clears them. */
const warehouseRequestFields = (
  type: DataAdaptorType | undefined,
  form: WarehouseFormFields,
): Partial<DataAdaptorSaveRequest> => {
  if (!isWarehouseAdaptorType(type)) return {};
  const gb = Number(form.maxScanGb);
  const authMode = AUTH_MODES[type!] ? form.authMode ?? defaultAuthMode(type) : undefined;
  return {
    catalog: CATALOG_TYPES.includes(type!) ? (form.catalog ?? "").trim() : "",
    ...(authMode ? { authMode } : {}),
    ...(form.keyJson?.trim() && usesKeyJson(type!, authMode) ? { keyJson: form.keyJson.trim() } : {}),
    httpPath: type === "DATABRICKS" ? (form.httpPath ?? "").trim() : "",
    computeWarehouse: type === "SNOWFLAKE" ? (form.computeWarehouse ?? "").trim() : "",
    role: type === "SNOWFLAKE" ? (form.role ?? "").trim() : "",
    extraJdbcProps: parseProps(form.extraPropsText),
    // Blank cache TTL sends -1 (server default); 0 disables caching.
    resultCacheTtlSeconds: form.cacheTtlText?.trim() ? Math.max(0, Math.round(Number(form.cacheTtlText))) : -1,
    dailyScanBytesLimit: (() => {
      const gb = Number(form.dailyScanGbText);
      return Number.isFinite(gb) && gb > 0 ? Math.round(gb * BYTES_PER_GB) : 0;
    })(),
    dailyQueryLimit: numberOrClear(form.dailyQueriesText),
    maxConcurrentAiQueries: numberOrClear(form.maxAiText),
    resourceGroup: (form.resourceGroup ?? "").trim(),
    requirePartitionFilter: !!form.requirePartitionFilter,
    ragSampleValues: !!form.ragSampleValues,
    allowEngineMl: ENGINE_ML_TYPES.includes(type!) ? !!form.allowEngineMl : false,
    kerberosServicePrincipal: authMode === "KERBEROS" ? (form.kerberosServicePrincipal ?? "").trim() : "",
    allowedSchemas: (form.allowedSchemasText ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    maxScanBytes: Number.isFinite(gb) && gb > 0 ? Math.round(gb * BYTES_PER_GB) : 0,
  };
};

const SSL_OPTIONS: SslOption[] = [
  { value: "disable", label: "disable" },
  { value: "prefer", label: "prefer" },
  { value: "require", label: "require" },
];

const defaultPortForType = (type: DataAdaptorType): number => {
  if (type === "MYSQL") return 3306;
  if (type === "MONGODB") return 27017;
  if (type === "TRINO") return 8080;
  if (type === "CLICKHOUSE") return 8123;
  if (type === "HIVE" || type === "SPARK_SQL") return 10000;
  if (type === "DATABRICKS") return 443;
  if (type === "REDSHIFT") return 5439;
  if (type === "SNOWFLAKE" || type === "BIGQUERY") return 0;
  return 5432;
};

const typeLabel = (type: DataAdaptorType): string =>
  TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type;

const emptyForm = (): DataAdaptorSaveRequest & WarehouseFormFields & { id?: string } => ({
  name: "",
  type: "POSTGRESQL",
  enabled: true,
  host: "",
  port: 5432,
  database: "",
  username: "",
  password: "",
  connectionUri: "",
  sslMode: "prefer",
  authDatabase: "admin",
  vpnProfileId: "",
  catalog: "",
  allowedSchemasText: "",
  maxScanGb: "",
  maxScanRowsText: "",
  maxResultRowsText: "",
  authMode: undefined,
  keyJson: "",
  keyJsonSet: false,
  httpPath: "",
  computeWarehouse: "",
  role: "",
  extraPropsText: "",
  cacheTtlText: "",
  timeoutText: "",
  dailyScanGbText: "",
  dailyQueriesText: "",
  maxAiText: "",
  resourceGroup: "",
  requirePartitionFilter: false,
  ragSampleValues: false,
  allowEngineMl: false,
  kerberosServicePrincipal: "",
  autoDisableEnabled: false,
  minSuccessPercentText: "80",
  minCallsText: "10",
  queryCallCount: 0,
  querySuccessCount: 0,
  queryFailureCount: 0,
  autoDisabledAt: null,
});

export default function AdminDataAdaptorsPage() {
  const { t } = useTranslation();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const packageLimits = usePackageLimits();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [items, setItems] = useState<DataAdaptor[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<
    DataAdaptorSaveRequest &
      WarehouseFormFields & {
      id?: string;
      vpnProfileName?: string | null;
      connectionUriSet?: boolean;
      connectionUriHosts?: string | null;
    }
  >(emptyForm());
  const [testing, setTesting] = useState(false);
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [explainOpen, setExplainOpen] = useState(false);
  const [schemaObjects, setSchemaObjects] = useState<AdaptorObjectNode[]>([]);
  const [schemaDescribe, setSchemaDescribe] = useState<AdaptorObjectDescribe | null>(null);
  const [schemaTreeLoading, setSchemaTreeLoading] = useState(false);
  const [schemaDescribeLoading, setSchemaDescribeLoading] = useState(false);
  const [schemaSearch, setSchemaSearch] = useState("");
  const [expandedSchemaName, setExpandedSchemaName] = useState<string | null>(null);
  const [queryHistory, setQueryHistory] = useState<ResourceQueryCallRow[]>([]);
  const [queryHistoryPage, setQueryHistoryPage] = useState(0);
  const [queryHistoryTotal, setQueryHistoryTotal] = useState(0);
  const [queryHistoryLoading, setQueryHistoryLoading] = useState(false);
  const [resettingStats, setResettingStats] = useState(false);

  const busy = testing || schemaTreeLoading;

  const filteredSchemaObjects = useMemo(() => {
    const q = schemaSearch.trim().toLowerCase();
    if (!q) return schemaObjects;
    return schemaObjects.filter((obj) => {
      const name = (obj.qualifiedName || obj.name || "").toLowerCase();
      const kind = (obj.kind || "").toLowerCase();
      const schema = (obj.schema || "").toLowerCase();
      return name.includes(q) || kind.includes(q) || schema.includes(q);
    });
  }, [schemaObjects, schemaSearch]);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await adminDataAdaptorService.list());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load()
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [load]);

  const loadQueryHistory = useCallback(async (id: string, page = 0) => {
    setQueryHistoryLoading(true);
    setQueryHistoryPage(page);
    try {
      const [calls, adaptor] = await Promise.all([
        adminDataAdaptorService.queryCalls(id, page),
        adminDataAdaptorService.get(id),
      ]);
      setQueryHistory(calls.content ?? []);
      setQueryHistoryTotal(calls.totalElements ?? 0);
      setForm((prev) =>
        prev.id === id
          ? {
              ...prev,
              queryCallCount: adaptor.queryCallCount ?? 0,
              querySuccessCount: adaptor.querySuccessCount ?? 0,
              queryFailureCount: adaptor.queryFailureCount ?? 0,
              autoDisabledAt: adaptor.autoDisabledAt ?? null,
            }
          : prev
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setQueryHistoryLoading(false);
    }
  }, []);

  const closeForm = () => {
    setFormOpen(false);
    setForm(emptyForm());
    setQueryHistory([]);
    setQueryHistoryPage(0);
    setQueryHistoryTotal(0);
    setError(null);
  };

  const startCreate = () => {
    setForm(emptyForm());
    setQueryHistory([]);
    setQueryHistoryPage(0);
    setQueryHistoryTotal(0);
    setMessage(null);
    setError(null);
    setFormOpen(true);
  };

  const save = async () => {
    setError(null);
    setMessage(null);
    const body: DataAdaptorSaveRequest = {
      name: form.name?.trim(),
      type: form.type,
      enabled: form.enabled,
      host: form.host?.trim(),
      port: form.port ? Number(form.port) : null,
      database: form.database?.trim(),
      username: form.username?.trim(),
      sslMode: form.sslMode,
      authDatabase: form.authDatabase,
      vpnProfileId: NO_VPN_TYPES.includes(form.type as DataAdaptorType) ? "" : form.vpnProfileId ?? "",
      ...queryLimitRequestFields(form),
      ...warehouseRequestFields(form.type, form),
      ...queryHealthRequestFields(form),
    };
    if (form.password?.trim()) body.password = form.password;
    if (form.connectionUri?.trim()) body.connectionUri = form.connectionUri.trim();
    try {
      if (form.id) {
        await adminDataAdaptorService.update(form.id, body);
        setMessage(t("adminDataAdaptors.updated"));
      } else {
        await adminDataAdaptorService.create(body);
        setMessage(t("adminDataAdaptors.created"));
      }
      setFormOpen(false);
      setForm(emptyForm());
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const edit = (item: DataAdaptor) => {
    setForm({
      id: item.id,
      name: item.name,
      type: item.type,
      enabled: item.enabled,
      host: item.host ?? "",
      port: item.port,
      database: item.database ?? "",
      username: item.username ?? "",
      password: "",
      connectionUri: "",
      connectionUriSet: item.connectionUriSet,
      connectionUriHosts: item.connectionUriHosts,
      sslMode: item.sslMode ?? "prefer",
      authDatabase: item.authDatabase ?? "admin",
      vpnProfileId: item.vpnProfileId ?? "",
      vpnProfileName: item.vpnProfileName,
      catalog: item.catalog ?? "",
      allowedSchemasText: (item.allowedSchemas ?? []).join(", "),
      maxScanGb: item.maxScanBytes ? String(Number((item.maxScanBytes / BYTES_PER_GB).toFixed(2))) : "",
      maxScanRowsText: item.maxScanRows ? String(item.maxScanRows) : "",
      maxResultRowsText: item.maxResultRows ? String(item.maxResultRows) : "",
      authMode: item.authMode ?? undefined,
      keyJson: "",
      keyJsonSet: item.keyJsonSet,
      httpPath: item.httpPath ?? "",
      computeWarehouse: item.computeWarehouse ?? "",
      role: item.role ?? "",
      extraPropsText: formatProps(item.extraJdbcProps),
      cacheTtlText: item.resultCacheTtlSeconds === null || item.resultCacheTtlSeconds === undefined
        ? "" : String(item.resultCacheTtlSeconds),
      timeoutText: item.queryTimeoutSeconds ? String(item.queryTimeoutSeconds) : "",
      dailyScanGbText: item.dailyScanBytesLimit
        ? String(Number((item.dailyScanBytesLimit / BYTES_PER_GB).toFixed(2))) : "",
      dailyQueriesText: item.dailyQueryLimit ? String(item.dailyQueryLimit) : "",
      maxAiText: item.maxConcurrentAiQueries ? String(item.maxConcurrentAiQueries) : "",
      resourceGroup: item.resourceGroup ?? "",
      requirePartitionFilter: !!item.requirePartitionFilter,
      ragSampleValues: !!item.ragSampleValues,
      allowEngineMl: !!item.allowEngineMl,
      kerberosServicePrincipal: item.kerberosServicePrincipal ?? "",
      autoDisableEnabled: !!item.autoDisableEnabled,
      minSuccessPercentText: item.autoDisableMinSuccessPercent != null ? String(item.autoDisableMinSuccessPercent) : "80",
      minCallsText: item.autoDisableMinCalls != null ? String(item.autoDisableMinCalls) : "10",
      queryCallCount: item.queryCallCount ?? 0,
      querySuccessCount: item.querySuccessCount ?? 0,
      queryFailureCount: item.queryFailureCount ?? 0,
      autoDisabledAt: item.autoDisabledAt ?? null,
    });
    setMessage(null);
    setError(null);
    setFormOpen(true);
    void loadQueryHistory(item.id);
  };

  const resetQueryStats = async () => {
    if (!form.id) return;
    setResettingStats(true);
    setError(null);
    try {
      const updated = await adminDataAdaptorService.resetQueryStats(form.id);
      setForm({
        ...form,
        queryCallCount: updated.queryCallCount ?? 0,
        querySuccessCount: updated.querySuccessCount ?? 0,
        queryFailureCount: updated.queryFailureCount ?? 0,
        autoDisabledAt: updated.autoDisabledAt ?? null,
      });
      setMessage(t("resourceQueryStats.resetOk"));
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setResettingStats(false);
    }
  };

  const clearStoredUri = async () => {
    if (!form.id) return;
    setError(null);
    setMessage(null);
    const body: DataAdaptorSaveRequest = {
      name: form.name?.trim(),
      type: form.type,
      enabled: form.enabled,
      host: form.host?.trim(),
      port: form.port ? Number(form.port) : null,
      database: form.database?.trim(),
      username: form.username?.trim(),
      sslMode: form.sslMode,
      authDatabase: form.authDatabase,
      vpnProfileId: NO_VPN_TYPES.includes(form.type as DataAdaptorType) ? "" : form.vpnProfileId ?? "",
      ...queryLimitRequestFields(form),
      ...warehouseRequestFields(form.type, form),
      ...queryHealthRequestFields(form),
    };
    try {
      const updated = await adminDataAdaptorService.update(form.id, body);
      setForm({
        ...form,
        connectionUri: "",
        connectionUriSet: updated.connectionUriSet,
        connectionUriHosts: updated.connectionUriHosts,
      });
      setMessage(t("adminDataAdaptors.connectionUriCleared"));
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const remove = async (item: DataAdaptor) => {
    try {
      await confirmDelete({ itemName: item.name });
      await adminDataAdaptorService.remove(item.id);
      if (form.id === item.id) {
        setFormOpen(false);
        setForm(emptyForm());
      }
      await load();
    } catch (e: unknown) {
      if (isCancelled(e)) return;
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const test = async () => {
    setTesting(true);
    setError(null);
    setMessage(null);
    try {
      let result: DataAdaptorTestResult;
      if (form.id) {
        // Test existing adaptor
        result = await adminDataAdaptorService.test(form.id);
      } else {
        // Test new adaptor without saving
        const body: DataAdaptorSaveRequest = {
          name: form.name?.trim(),
          type: form.type,
          enabled: form.enabled,
          host: form.host?.trim(),
          port: form.port ? Number(form.port) : null,
          database: form.database?.trim(),
          username: form.username?.trim(),
          sslMode: form.sslMode,
          authDatabase: form.authDatabase,
          vpnProfileId: NO_VPN_TYPES.includes(form.type as DataAdaptorType) ? "" : form.vpnProfileId ?? "",
          ...queryLimitRequestFields(form),
          ...warehouseRequestFields(form.type, form),
          ...queryHealthRequestFields(form),
        };
        if (form.password?.trim()) body.password = form.password;
        if (form.connectionUri?.trim()) body.connectionUri = form.connectionUri.trim();
        result = await adminDataAdaptorService.testWithRequest(body);
      }
      if (!result.ok) {
        setError(
          result.vpnOk === false
            ? t("adminDataAdaptors.vpnTestFailed", { message: result.vpnMessage ?? result.message })
            : result.message
        );
        return;
      }
      const base = result.message || t("adminDataAdaptors.testOk");
      setMessage(result.vpnOk ? `${t("adminDataAdaptors.vpnTestOk")} · ${base}` : base);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(false);
    }
  };

  const type = (form.type ?? "POSTGRESQL") as DataAdaptorType;
  const authModes = AUTH_MODES[type];
  const authMode = authModes ? form.authMode ?? authModes[0] : undefined;
  const secretLabelKey =
    authMode === "TOKEN"
      ? "token"
      : authMode === "OAUTH_M2M"
        ? "clientSecret"
        : authMode === "KEY_PAIR"
          ? "keyPassphrase"
          : authMode === "KERBEROS"
            ? "unused"
            : "password";

  const rowActions = (item: DataAdaptor) => (
    <Stack direction="row" spacing={0.25} justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
      <AppIconButton size="small" aria-label={t("common.edit")} onClick={() => edit(item)}>
        <EditOutlinedIcon fontSize="small" />
      </AppIconButton>
      <AppIconButton
        size="small"
        aria-label={t("common.delete")}
        sx={formRowButtonSx}
        onClick={() => void remove(item)}
      >
        <DeleteOutlineOutlinedIcon fontSize="small" />
      </AppIconButton>
    </Stack>
  );

  return (
    <PageListContainer>
      <PageHeader
        title={t("adminDataAdaptors.title")}
        subtitle={t("adminDataAdaptors.subtitle")}
        actions={
          <ResponsiveActions
            items={[
              {
                key: "create",
                label: t("adminDataAdaptors.newAdaptor"),
                icon: "add",
                primary: true,
                disabled: busy,
                onClick: startCreate,
              },
            ]}
          />
        }
      />

      {packageLimits && (
        <PackageLimitHint group="count" capped={packageLimits.maxDataAdaptors != null}>
          {t("packageLimits.dataAdaptors", {
            count: formatLimitValue(packageLimits.maxDataAdaptors, t("packageLimits.unlimited")),
          })}
        </PackageLimitHint>
      )}

      {error && !formOpen && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      <PageSectionCard title={t("adminDataAdaptors.listTitle")} noPadding>
        {loading ? (
          <Stack alignItems="center" py={5}>
            <CircularProgress size={32} />
          </Stack>
        ) : items.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <PageEmptyState
              message={t("adminDataAdaptors.empty")}
              actionLabel={t("adminDataAdaptors.create")}
              onAction={startCreate}
              actionIcon={<AddOutlinedIcon />}
            />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ display: { xs: "none", sm: "block" }, maxWidth: "100%" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }} />
                    <TableCell>{t("adminDataAdaptors.name")}</TableCell>
                    <TableCell>{t("adminDataAdaptors.type")}</TableCell>
                    <TableCell>{t("adminDataAdaptors.host")}</TableCell>
                    <TableCell>{t("adminDataAdaptors.enabled")}</TableCell>
                    <TableCell>{t("resourceQueryStats.title")}</TableCell>
                    <TableCell align="right">{t("common.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} hover onClick={() => edit(item)} sx={{ cursor: "pointer" }}>
                      <TableCell sx={{ width: 56, py: 1 }}>
                        <AdaptorTypeIcon type={item.type} size={34} label={typeLabel(item.type)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {item.database || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{typeLabel(item.type)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {item.host || "—"}
                        </Typography>
                        {item.vpnProfileName && (
                          <Chip size="small" variant="outlined" label={`VPN: ${item.vpnProfileName}`} sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <AdaptorStatusChip enabled={item.enabled} autoDisabledAt={item.autoDisabledAt} />
                      </TableCell>
                      <TableCell sx={{ py: 0.75, verticalAlign: "middle" }}>
                        <AdaptorStatsSummary
                          total={item.queryCallCount}
                          success={item.querySuccessCount}
                          failure={item.queryFailureCount}
                        />
                      </TableCell>
                      <TableCell align="right">{rowActions(item)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack spacing={1.5} sx={{ display: { xs: "flex", sm: "none" }, p: 2 }}>
              {items.map((item) => (
                <Box
                  key={item.id}
                  onClick={() => edit(item)}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    cursor: "pointer",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                      <AdaptorTypeIcon type={item.type} size={34} label={typeLabel(item.type)} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {typeLabel(item.type)} · {item.host || "—"}
                          {item.vpnProfileName ? ` · VPN: ${item.vpnProfileName}` : ""}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 0.75 }}>
                          <AdaptorStatusChip enabled={item.enabled} autoDisabledAt={item.autoDisabledAt} />
                          <AdaptorStatsSummary
                            total={item.queryCallCount}
                            success={item.querySuccessCount}
                            failure={item.queryFailureCount}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                    {rowActions(item)}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </PageSectionCard>

      <AppSlideOver
        open={formOpen}
        onClose={closeForm}
        size="xl"
        title={form.id ? t("adminDataAdaptors.edit") : t("adminDataAdaptors.create")}
        actions={
          <>
            <AppButton variant="outlined" onClick={closeForm} disabled={busy}>
              {t("common.cancel")}
            </AppButton>
            <AppButton onClick={() => void save()} disabled={busy}>
              {form.id ? t("common.save") : t("common.create")}
            </AppButton>
          </>
        }
      >
          {error && formOpen && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Stack spacing={2.5}>
              <AppFormGrid>
                <AppFormField size={GRID_HALF}>
                  <AppTextField
                    label={t("adminDataAdaptors.name")}
                    value={form.name ?? ""}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    fullWidth
                  />
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AdaptorTypeIcon type={type} size={36} label={typeLabel(type)} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <AppAutocomplete<TypeOption>
                        label={t("adminDataAdaptors.type")}
                        options={TYPE_OPTIONS}
                        value={TYPE_OPTIONS.find((option) => option.value === type) ?? TYPE_OPTIONS[0]}
                        disableClearable
                        autoHighlight
                        openOnFocus
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(a, b) => a.value === b.value}
                        renderOption={(props, option) => (
                          <li {...props} key={option.value}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <AdaptorTypeIcon type={option.value} size={28} label={option.label} />
                              <span>{option.label}</span>
                            </Stack>
                          </li>
                        )}
                        onChange={(_, option) => {
                          if (!option) return;
                          const next = option.value;
                          setForm({
                            ...form,
                            type: next,
                            port: defaultPortForType(next) || null,
                            authMode: defaultAuthMode(next),
                          });
                        }}
                      />
                    </Box>
                  </Stack>
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <AppToggleField
                    label={t("adminDataAdaptors.enabled")}
                    value={form.enabled ? "true" : "false"}
                    onChange={(v) => setForm({ ...form, enabled: v === "true" })}
                    options={[
                      { value: "true", label: t("adminDataAdaptors.active") },
                      { value: "false", label: t("adminDataAdaptors.passive") },
                    ]}
                  />
                </AppFormField>
                {type === "MONGODB" && (
                  <>
                    {form.id && form.connectionUriSet && (
                      <AppFormField size={GRID_FULL}>
                        <Alert
                          severity="info"
                          action={
                            <AppButton size="small" onClick={clearStoredUri} disabled={busy}>
                              {t("adminDataAdaptors.clearStoredUri")}
                            </AppButton>
                          }
                        >
                          {form.connectionUriHosts
                            ? t("adminDataAdaptors.connectionUriStored", {
                                hosts: form.connectionUriHosts,
                              })
                            : t("adminDataAdaptors.connectionUriStoredUnknown")}
                        </Alert>
                      </AppFormField>
                    )}
                    <AppFormField size={GRID_FULL}>
                      <AppTextField
                        label={t("adminDataAdaptors.connectionUri")}
                        type="password"
                        autoComplete="new-password"
                        value={form.connectionUri ?? ""}
                        onChange={(e) => setForm({ ...form, connectionUri: e.target.value })}
                        fullWidth
                        helperText={
                          form.id
                            ? t("adminDataAdaptors.connectionUriEditHelp")
                            : t("adminDataAdaptors.connectionUriHelp")
                        }
                      />
                    </AppFormField>
                  </>
                )}
                <AppFormField size={GRID_HALF}>
                  <AppTextField
                    label={t("adminDataAdaptors.host")}
                    value={form.host ?? ""}
                    onChange={(e) => setForm({ ...form, host: e.target.value })}
                    fullWidth
                    helperText={
                      type === "BIGQUERY" || type === "SNOWFLAKE" || type === "DATABRICKS"
                        ? t(`adminDataAdaptors.hostHelpByType.${type}`)
                        : undefined
                    }
                  />
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <AppTextField
                    label={t("adminDataAdaptors.port")}
                    type="number"
                    value={form.port ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, port: e.target.value ? Number(e.target.value) : null })
                    }
                    fullWidth
                  />
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <AppTextField
                    label={t("adminDataAdaptors.database")}
                    value={form.database ?? ""}
                    onChange={(e) => setForm({ ...form, database: e.target.value })}
                    fullWidth
                    helperText={
                      isWarehouseAdaptorType(type)
                        ? t(`adminDataAdaptors.databaseHelpByType.${type}`, {
                            defaultValue: t("adminDataAdaptors.warehouseDatabaseHelp"),
                          })
                        : undefined
                    }
                  />
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <AppTextField
                    label={t("adminDataAdaptors.username")}
                    value={form.username ?? ""}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    fullWidth
                  />
                </AppFormField>
                {secretLabelKey !== "unused" && (
                  <AppFormField size={GRID_HALF}>
                    <AppTextField
                      label={t(`adminDataAdaptors.secretLabel.${secretLabelKey}`)}
                      type="password"
                      autoComplete="new-password"
                      value={form.password ?? ""}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      fullWidth
                      helperText={form.id ? t("adminDataAdaptors.secretKeepHint") : undefined}
                    />
                  </AppFormField>
                )}
                {(type === "POSTGRESQL" || type === "MYSQL" || isWarehouseAdaptorType(type)) && (
                  <AppFormField size={GRID_HALF}>
                    <AppAutocomplete<SslOption>
                      label={t("adminDataAdaptors.sslMode")}
                      options={SSL_OPTIONS}
                      value={
                        SSL_OPTIONS.find((option) => option.value === (form.sslMode ?? "prefer")) ??
                        SSL_OPTIONS[1]
                      }
                      disableClearable
                      autoHighlight
                      openOnFocus
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      onChange={(_, option) => {
                        if (!option) return;
                        setForm({ ...form, sslMode: option.value });
                      }}
                    />
                  </AppFormField>
                )}
                {type === "MONGODB" && (
                  <AppFormField size={GRID_HALF}>
                    <AppTextField
                      label={t("adminDataAdaptors.authDatabase")}
                      value={form.authDatabase ?? ""}
                      onChange={(e) => setForm({ ...form, authDatabase: e.target.value })}
                      fullWidth
                    />
                  </AppFormField>
                )}
                <AppFormField size={GRID_FULL}>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    {t("adminDataAdaptors.queryLimits.title")}
                  </Typography>
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <AppTextField
                    label={t("adminDataAdaptors.runtime.timeout")}
                    type="number"
                    value={form.timeoutText ?? ""}
                    onChange={(e) => setForm({ ...form, timeoutText: e.target.value })}
                    fullWidth
                    helperText={t("adminDataAdaptors.queryLimits.timeoutHelp")}
                  />
                </AppFormField>
                <AppFormField size={GRID_HALF}>
                  <AppTextField
                    label={t("adminDataAdaptors.maxResultRows")}
                    type="number"
                    value={form.maxResultRowsText ?? ""}
                    onChange={(e) => setForm({ ...form, maxResultRowsText: e.target.value })}
                    fullWidth
                    helperText={t("adminDataAdaptors.maxResultRowsHelp")}
                  />
                </AppFormField>
                {type !== "BIGQUERY" && type !== "SNOWFLAKE" && (
                  <AppFormField size={GRID_HALF}>
                    <AppTextField
                      label={t("adminDataAdaptors.maxScanRows")}
                      type="number"
                      value={form.maxScanRowsText ?? ""}
                      onChange={(e) => setForm({ ...form, maxScanRowsText: e.target.value })}
                      fullWidth
                      helperText={t("adminDataAdaptors.maxScanRowsHelp")}
                    />
                  </AppFormField>
                )}
                <AppFormField size={GRID_FULL}>
                  <ResourceQueryStatsPanel
                    saved={!!form.id}
                    stats={{
                      queryCallCount: form.queryCallCount,
                      querySuccessCount: form.querySuccessCount,
                      queryFailureCount: form.queryFailureCount,
                      autoDisabledAt: form.autoDisabledAt,
                    }}
                    history={queryHistory}
                    historyPage={queryHistoryPage}
                    historyTotal={queryHistoryTotal}
                    onHistoryPageChange={(page) => {
                      if (form.id) void loadQueryHistory(form.id, page);
                    }}
                    historyLoading={queryHistoryLoading}
                    resetting={resettingStats}
                    onReset={() => void resetQueryStats()}
                    autoDisableEnabled={!!form.autoDisableEnabled}
                    minSuccessPercent={form.minSuccessPercentText ?? ""}
                    minCalls={form.minCallsText ?? ""}
                    onAutoDisableChange={(patch) =>
                      setForm({
                        ...form,
                        ...(patch.autoDisableEnabled != null ? { autoDisableEnabled: patch.autoDisableEnabled } : {}),
                        ...(patch.minSuccessPercent != null ? { minSuccessPercentText: patch.minSuccessPercent } : {}),
                        ...(patch.minCalls != null ? { minCallsText: patch.minCalls } : {}),
                      })
                    }
                  />
                </AppFormField>
                {isWarehouseAdaptorType(type) && (
                  <>
                    {authModes && (
                      <AppFormField size={GRID_HALF}>
                        <AppAutocomplete<{ value: AdaptorAuthMode; label: string }>
                          label={t("adminDataAdaptors.authMode")}
                          options={authModes.map((m) => ({ value: m, label: t(`adminDataAdaptors.authModes.${m}`) }))}
                          value={{ value: authMode!, label: t(`adminDataAdaptors.authModes.${authMode}`) }}
                          disableClearable
                          getOptionLabel={(option) => option.label}
                          isOptionEqualToValue={(a, b) => a.value === b.value}
                          onChange={(_, option) => {
                            if (!option) return;
                            setForm({ ...form, authMode: option.value });
                          }}
                        />
                      </AppFormField>
                    )}
                    {CATALOG_TYPES.includes(type) && (
                      <AppFormField size={GRID_HALF}>
                        <AppTextField
                          label={t(`adminDataAdaptors.catalogLabel.${type}`)}
                          value={form.catalog ?? ""}
                          onChange={(e) => setForm({ ...form, catalog: e.target.value })}
                          fullWidth
                          required={type !== "DATABRICKS"}
                          helperText={t(`adminDataAdaptors.catalogHelpByType.${type}`)}
                        />
                      </AppFormField>
                    )}
                    {type === "DATABRICKS" && (
                      <AppFormField size={GRID_HALF}>
                        <AppTextField
                          label={t("adminDataAdaptors.httpPath")}
                          value={form.httpPath ?? ""}
                          onChange={(e) => setForm({ ...form, httpPath: e.target.value })}
                          fullWidth
                          required
                          placeholder="/sql/1.0/warehouses/abc123"
                          helperText={t("adminDataAdaptors.httpPathHelp")}
                        />
                      </AppFormField>
                    )}
                    {type === "SNOWFLAKE" && (
                      <>
                        <AppFormField size={GRID_HALF}>
                          <AppTextField
                            label={t("adminDataAdaptors.computeWarehouse")}
                            value={form.computeWarehouse ?? ""}
                            onChange={(e) => setForm({ ...form, computeWarehouse: e.target.value })}
                            fullWidth
                          />
                        </AppFormField>
                        <AppFormField size={GRID_HALF}>
                          <AppTextField
                            label={t("adminDataAdaptors.role")}
                            value={form.role ?? ""}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            fullWidth
                          />
                        </AppFormField>
                      </>
                    )}
                    {usesKeyJson(type, authMode) && (
                      <AppFormField size={GRID_FULL}>
                        <AppTextField
                          label={t(
                            authMode === "KERBEROS"
                              ? "adminDataAdaptors.keytabBase64"
                              : type === "BIGQUERY"
                                ? "adminDataAdaptors.serviceAccountJson"
                                : "adminDataAdaptors.privateKeyPem"
                          )}
                          value={form.keyJson ?? ""}
                          onChange={(e) => setForm({ ...form, keyJson: e.target.value })}
                          fullWidth
                          multiline
                          minRows={3}
                          maxRows={10}
                          autoComplete="off"
                          inputProps={{ spellCheck: false, style: { fontFamily: "monospace", fontSize: 12 } }}
                          helperText={
                            form.keyJsonSet ? t("adminDataAdaptors.keyJsonStored") : t("adminDataAdaptors.keyJsonHelp")
                          }
                        />
                      </AppFormField>
                    )}
                    <AppFormField size={GRID_FULL}>
                      <AppTextField
                        label={t(type === "BIGQUERY" ? "adminDataAdaptors.allowedDatasets" : "adminDataAdaptors.allowedSchemas")}
                        value={form.allowedSchemasText ?? ""}
                        onChange={(e) => setForm({ ...form, allowedSchemasText: e.target.value })}
                        fullWidth
                        helperText={t("adminDataAdaptors.allowedSchemasHelp")}
                      />
                    </AppFormField>
                    {SCAN_BYTES_TYPES.includes(type) && (
                      <AppFormField size={GRID_HALF}>
                        <AppTextField
                          label={t("adminDataAdaptors.maxScanGb")}
                          type="number"
                          value={form.maxScanGb ?? ""}
                          onChange={(e) => setForm({ ...form, maxScanGb: e.target.value })}
                          fullWidth
                          helperText={t(type === "BIGQUERY" ? "adminDataAdaptors.maxScanGbHelpBigQuery" : "adminDataAdaptors.maxScanGbHelp")}
                        />
                      </AppFormField>
                    )}
                    {authMode === "KERBEROS" && (
                      <AppFormField size={GRID_FULL}>
                        <AppTextField
                          label={t("adminDataAdaptors.kerberosServicePrincipal")}
                          value={form.kerberosServicePrincipal ?? ""}
                          onChange={(e) => setForm({ ...form, kerberosServicePrincipal: e.target.value })}
                          fullWidth
                          required
                          placeholder="hive/_HOST@EXAMPLE.COM"
                          helperText={t("adminDataAdaptors.kerberosHelp")}
                        />
                      </AppFormField>
                    )}
                    <AppFormField size={GRID_FULL}>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>
                        {t("adminDataAdaptors.runtime.title")}
                      </Typography>
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField
                        label={t("adminDataAdaptors.runtime.cacheTtl")}
                        type="number"
                        value={form.cacheTtlText ?? ""}
                        onChange={(e) => setForm({ ...form, cacheTtlText: e.target.value })}
                        fullWidth
                        helperText={t("adminDataAdaptors.runtime.cacheTtlHelp")}
                      />
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField
                        label={t("adminDataAdaptors.runtime.dailyScanGb")}
                        type="number"
                        value={form.dailyScanGbText ?? ""}
                        onChange={(e) => setForm({ ...form, dailyScanGbText: e.target.value })}
                        fullWidth
                        helperText={t("adminDataAdaptors.runtime.dailyScanGbHelp")}
                      />
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField
                        label={t("adminDataAdaptors.runtime.dailyQueries")}
                        type="number"
                        value={form.dailyQueriesText ?? ""}
                        onChange={(e) => setForm({ ...form, dailyQueriesText: e.target.value })}
                        fullWidth
                      />
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppTextField
                        label={t("adminDataAdaptors.runtime.maxAi")}
                        type="number"
                        value={form.maxAiText ?? ""}
                        onChange={(e) => setForm({ ...form, maxAiText: e.target.value })}
                        fullWidth
                        helperText={t("adminDataAdaptors.runtime.maxAiHelp")}
                      />
                    </AppFormField>
                    {type !== "DATABRICKS" && (
                      <AppFormField size={GRID_HALF}>
                        <AppTextField
                          label={t("adminDataAdaptors.runtime.resourceGroup")}
                          value={form.resourceGroup ?? ""}
                          onChange={(e) => setForm({ ...form, resourceGroup: e.target.value })}
                          fullWidth
                          helperText={t(`adminDataAdaptors.runtime.resourceGroupHelp.${type}`)}
                        />
                      </AppFormField>
                    )}
                    <AppFormField size={GRID_HALF}>
                      <AppToggleField
                        label={t("adminDataAdaptors.runtime.requirePartitionFilter")}
                        value={form.requirePartitionFilter ? "true" : "false"}
                        onChange={(v) => setForm({ ...form, requirePartitionFilter: v === "true" })}
                        options={[
                          { value: "true", label: t("common.yes") },
                          { value: "false", label: t("common.no") },
                        ]}
                      />
                    </AppFormField>
                    <AppFormField size={GRID_HALF}>
                      <AppToggleField
                        label={t("adminDataAdaptors.runtime.ragSampleValues")}
                        value={form.ragSampleValues ? "true" : "false"}
                        onChange={(v) => setForm({ ...form, ragSampleValues: v === "true" })}
                        options={[
                          { value: "true", label: t("common.yes") },
                          { value: "false", label: t("common.no") },
                        ]}
                      />
                    </AppFormField>
                    {ENGINE_ML_TYPES.includes(type) && (
                      <AppFormField size={GRID_HALF}>
                        <AppToggleField
                          label={t("adminDataAdaptors.runtime.allowEngineMl")}
                          value={form.allowEngineMl ? "true" : "false"}
                          onChange={(v) => setForm({ ...form, allowEngineMl: v === "true" })}
                          options={[
                            { value: "true", label: t("common.yes") },
                            { value: "false", label: t("common.no") },
                          ]}
                        />
                      </AppFormField>
                    )}
                    <AppFormField size={GRID_FULL}>
                      <AppTextField
                        label={t("adminDataAdaptors.extraProps")}
                        value={form.extraPropsText ?? ""}
                        onChange={(e) => setForm({ ...form, extraPropsText: e.target.value })}
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder={type === "BIGQUERY" ? "location=EU" : "key=value"}
                        inputProps={{ spellCheck: false, style: { fontFamily: "monospace", fontSize: 12 } }}
                        helperText={t(type === "BIGQUERY" ? "adminDataAdaptors.extraPropsHelpBigQuery" : "adminDataAdaptors.extraPropsHelp")}
                      />
                    </AppFormField>
                  </>
                )}
                {!NO_VPN_TYPES.includes(type) && (
                  <AppFormField size={GRID_FULL}>
                    <VpnProfileSelect
                      value={form.vpnProfileId ?? ""}
                      currentName={form.vpnProfileName}
                      onChange={(vpnProfileId) => setForm({ ...form, vpnProfileId })}
                    />
                  </AppFormField>
                )}
              </AppFormGrid>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <AppButton variant="outlined" onClick={() => void test()} disabled={busy}>
                  {testing ? t("common.loading") : t("adminDataAdaptors.testConnection")}
                </AppButton>
                {isWarehouseAdaptorType(type) && form.id && (
                  <AppButton variant="outlined" onClick={() => setExplainOpen(true)} disabled={busy}>
                    {t("adminDataAdaptors.explain.button")}
                  </AppButton>
                )}
                {form.id && (
                  <AppButton
                    variant="outlined"
                    disabled={busy}
                    onClick={() => {
                      if (!form.id) return;
                      setSchemaOpen(true);
                      setSchemaDescribe(null);
                      setSchemaSearch("");
                      setExpandedSchemaName(null);
                      setSchemaTreeLoading(true);
                      adminDataAdaptorService
                        .metadataTree(form.id)
                        .then((tree) => setSchemaObjects(flattenAdaptorLeafObjects(tree.roots)))
                        .catch((e: unknown) =>
                          setError(e instanceof Error ? e.message : String(e))
                        )
                        .finally(() => setSchemaTreeLoading(false));
                    }}
                  >
                    {t("adminDataAdaptors.browseSchema")}
                  </AppButton>
                )}
              </Stack>

          </Stack>
      </AppSlideOver>

      <AppSlideOver
        open={schemaOpen}
        onClose={() => {
          setSchemaOpen(false);
          setSchemaSearch("");
          setExpandedSchemaName(null);
          setSchemaDescribe(null);
        }}
        size="xl"
        title={t("adminDataAdaptors.browseSchema")}
      >
          <Box sx={{ pb: 1 }}>
            <AppTextField
              label={t("adminDataAdaptors.schemaSearch")}
              placeholder={t("adminDataAdaptors.schemaSearchPlaceholder")}
              value={schemaSearch}
              onChange={(e) => setSchemaSearch(e.target.value)}
              fullWidth
              size="small"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
              {t("adminDataAdaptors.schemaSearchCount", {
                shown: filteredSchemaObjects.length,
                total: schemaObjects.length,
              })}
            </Typography>
          </Box>

          <Box sx={{ px: 0, pb: 1 }}>
            {schemaTreeLoading ? (
              <Stack alignItems="center" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Stack>
            ) : filteredSchemaObjects.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2 }}>
                {schemaObjects.length === 0
                  ? t("adminDataAdaptors.schemaEmpty")
                  : t("adminDataAdaptors.schemaSearchNoResults")}
              </Typography>
            ) : (
              <List dense disablePadding>
                {filteredSchemaObjects.map((obj) => {
                  const name = obj.qualifiedName || obj.name;
                  const expanded = expandedSchemaName === name;
                  return (
                    <Box
                      key={name}
                      sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                      }}
                    >
                      <ListItemButton
                        selected={expanded}
                        onClick={() => {
                          if (!form.id) return;
                          if (expanded) {
                            setExpandedSchemaName(null);
                            setSchemaDescribe(null);
                            return;
                          }
                          setExpandedSchemaName(name);
                          setSchemaDescribe(null);
                          setSchemaDescribeLoading(true);
                          adminDataAdaptorService
                            .describeObject(form.id, name)
                            .then(setSchemaDescribe)
                            .catch((e: unknown) =>
                              setError(e instanceof Error ? e.message : String(e))
                            )
                            .finally(() => setSchemaDescribeLoading(false));
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {expanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={name}
                          secondary={obj.kind || undefined}
                          primaryTypographyProps={{ sx: { wordBreak: "break-word" } }}
                        />
                      </ListItemButton>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 2, pb: 1.5, pl: 6 }}>
                          {schemaDescribeLoading ? (
                            <Stack direction="row" alignItems="center" spacing={1} py={1}>
                              <CircularProgress size={18} />
                              <Typography variant="caption" color="text.secondary">
                                {t("common.loading")}
                              </Typography>
                            </Stack>
                          ) : schemaDescribe ? (
                            (schemaDescribe.columns || []).length === 0 ? (
                              <Typography variant="body2" color="text.secondary" py={1}>
                                {t("adminDataAdaptors.schemaNoColumns")}
                              </Typography>
                            ) : (
                              <TableContainer
                                sx={{
                                  maxHeight: 280,
                                  overflow: "auto",
                                  border: 1,
                                  borderColor: "divider",
                                  borderRadius: 1,
                                }}
                              >
                                <Table size="small" stickyHeader>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>{t("adminDataAdaptors.schemaColumn")}</TableCell>
                                      <TableCell>{t("adminDataAdaptors.schemaType")}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(schemaDescribe.columns || []).map((col) => (
                                      <TableRow key={col.name}>
                                        <TableCell>
                                          {col.name}
                                          {col.partitionKey && (
                                            <Chip
                                              size="small"
                                              color="warning"
                                              variant="outlined"
                                              label={t("adminDataAdaptors.partitionBadge")}
                                              sx={{ ml: 1, height: 18, fontSize: 11 }}
                                            />
                                          )}
                                        </TableCell>
                                        <TableCell>{col.dataType}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            )
                          ) : null}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            )}
          </Box>
      </AppSlideOver>
      {form.id && (
        <AppSlideOver
          open={explainOpen}
          onClose={() => setExplainOpen(false)}
          size="xl"
          title={t("adminDataAdaptors.explain.run")}
        >
          {explainOpen && <WarehouseExplainPanel adaptorId={form.id} />}
        </AppSlideOver>
      )}
    </PageListContainer>
  );
}
